import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { useFormatTime } from '@/src/hooks/srp/formatTime'
import { useGetRTSolutionCardData } from '@/src/hooks/srp/RTSolutionView/getRTSolutionCardData'
import { FlightCard } from '@/src/types/flight'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import { Box, LinearProgress, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

const TransitionLoader = ({
    onwardFlightData,
    selectedOnwardFareFamilyPlan
}: {
    onwardFlightData: FlightCard
    selectedOnwardFareFamilyPlan: Plan | null
}) => {
    const formatTime = useFormatTime()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const flightData = useGetRTSolutionCardData(onwardFlightData)
    const tCommon = useTranslations('common')
    const price = selectedOnwardFareFamilyPlan?.price || flightData.price
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: '600px',
                    gap: 4,
                    animation: 'fadeIn 0.3s ease-in',
                    '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(20px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                    }
                }}
            >
                {/* Selected Flight Card */}
                <Box
                    sx={{
                        width: '100%',
                        p: 1,
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box display={'flex'} maxWidth={150} overflow={'auto'} gap={0.5}>
                                {getAirlineIcons(flightData.airlineCodes, 35, 35)}
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {flightData.airlineName}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight={700} color="primary">
                                {formatCurrency(price)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={600}>
                                {flightData.departureTime}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {flightData.departureAirport}
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, px: 2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    sx={{
                                        height: '2px',
                                        bgcolor: 'grey.300',
                                        width: '100%'
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '-20px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        bgcolor: 'white',
                                        px: 1,
                                        color: 'text.secondary'
                                    }}
                                >
                                    {formatTime(flightData.totalDuration)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={600}>
                                {flightData.arrivalTime}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {flightData.arrivalAirport}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Loading Progress Bar */}
                <Box sx={{ width: '100%', maxWidth: '400px' }}>
                    <LinearProgress
                        variant="indeterminate"
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4
                            }
                        }}
                    />
                    <Typography
                        variant="body1"
                        sx={{
                            mt: 2,
                            textAlign: 'center',
                            color: 'text.primary',
                            fontWeight: 600,
                            fontSize: '1rem'
                        }}
                    >
                        {tCommon('findingBestReturnFlights')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

export { TransitionLoader }
